/**
 * Helpers for the raid launcher's loss readout.
 *
 * The backend's `/api/raids/estimate` returns `avgLosses` = the MEAN number of
 * the player's own ships sunk, over 400 seeded combat simulations. It is an
 * expectation, NOT a probability: 0.2 does not mean "20% chance to lose a ship"
 * (that only holds if you can never lose two at once). And winning a raid means
 * the enemy is wiped out — never that you come back unscathed. These helpers
 * turn that raw mean into something a player can read at a glance.
 */

export type LossSeverity = 'none' | 'light' | 'moderate' | 'heavy'

/**
 * Bucket losses RELATIVE to fleet size — losing ~1 ship out of 2 is
 * catastrophic, out of 10 is noise. Fed the worst-case bound (p90) so the
 * colour reflects how bad it can realistically get, not just the average.
 * Below 0.05 we treat it as "no losses".
 */
export function lossSeverity(losses: number, fleetSize: number): LossSeverity {
  if (losses < 0.05) return 'none'
  const ratio = fleetSize > 0 ? losses / fleetSize : 1
  if (ratio <= 0.25) return 'light'
  if (ratio <= 0.5) return 'moderate'
  return 'heavy'
}

/** Tailwind text colour per severity — neutral green up to the alarming rum-500. */
export const LOSS_SEVERITY_CLASS: Record<LossSeverity, string> = {
  none: 'text-green-300',
  light: 'text-sand-300',
  moderate: 'text-rum-300',
  heavy: 'text-rum-500',
}

/**
 * French wording for the mean losses: "Aucune", "~0,2 navire", "~2,4 navires".
 * Singular below 2 (French pluralises from 2), comma decimal separator.
 */
export function formatAvgLosses(avgLosses: number): string {
  if (avgLosses < 0.05) return 'Aucune'
  const n = avgLosses.toFixed(1).replace('.', ',')
  return `~${n} navire${avgLosses >= 2 ? 's' : ''}`
}

/**
 * French wording for the likely-losses RANGE (integer ship counts):
 * "Aucune" (high 0), "1 navire" (low === high), "0–2 navires" otherwise.
 * Plural follows the high bound (French pluralises from 2).
 */
export function formatLossRange(low: number, high: number): string {
  if (high <= 0) return 'Aucune'
  const plural = high >= 2 ? 's' : ''
  if (low >= high) return `${high} navire${plural}`
  return `${low}–${high} navire${plural}`
}
